import { CollectionItemSettings } from "@pages/Property/PropertyCollectionPage/CollectionItemPage/CollectionItemSettings/CollectionItemSettings";
import { CompanyCollectionData } from "@src/to-be-removed/LocalData/Company/Collections/CompanyCollectionData";
import { CompanyCollectionItemData } from "@src/to-be-removed/LocalData/Company/Collections/CompanyCollectionItemData";
import { Tag } from "data-hooks/company/tags/tags.type";
import { query, where } from "firebase/firestore";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "@components/deprecated/DataTable/DataTable";
import { DataTableFileDocumentsCell } from "@components/deprecated/DataTable/FirebaseDataTable/PrebuiltDataTableCells/DataTableFileDocumentsCell";
import { Popup } from "@components/deprecated/Popup/Popup";
import { PopupTemplates } from "@components/deprecated/PopupTemplates/PopupTemplates";
import { ReadAccessPopup } from "@components/deprecated/ReadAccessPopup/ReadAccessPopup";
import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";
import { CollectionUtilities } from "@utils/collectionUtilities";
import { SystemsAndComponentsUtilities } from "@utils/systemsAndComponentsUtilities";
import { Utilities } from "@utils/utilities";
import { CompanyCustomFieldData } from "../../../Company/Collections/CompanyCustomFieldData";
import { CustomFieldDataType } from "../../../Company/Collections/CustomFieldDataType";
import { CustomFieldDateDataType } from "../../../Company/Collections/CustomFieldDataTypeHelper/CustomFieldDateDataType";
import { CompanyData } from "../../../Company/CompanyData";
import { DataTypeData } from "../../../Company/DataTypeData";
import { PropertyCollectionData } from "../../../Property/Collections/PropertyCollectionData";
import { PropertyCollectionItemData } from "../../../Property/Collections/PropertyCollectionItemData";
import { PropertyCustomFieldData } from "../../../Property/Collections/PropertyCustomFieldData";
import { PropertyData } from "../../../Property/PropertyData";
import CollectionLoader from "../../CollectionLoader";
import { DataObjectTagField } from "../../DataObjectField/CustomFields/DeviationDataFields";
import { FirebaseDataCollectionItemFields } from "../../DataObjectField/CustomFields/FirebaseDataCollectionItemFields";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataCustomDataTypeFields } from "../../DataObjectField/CustomFields/FirebaseDataCustomDataTypeFields";
import { FirebaseDataFileDocumentField } from "../../DataObjectField/CustomFields/FirebaseDataFileDocumentField";
import { FirebaseDataNorwegianStandardField } from "../../DataObjectField/CustomFields/FirebaseDataNorwegianStandardField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataUnitField } from "../../DataObjectField/CustomFields/FirebaseDataUnitField";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { ValueDataSource } from "../../DataSource";
import { FirebaseFile } from "../../FirebaseFile";
import { LocalData } from "../../LocalData";
import { PropertyFileData } from "../../PropertyFileData";

export type GenericCollectionItemType = PropertyCollectionItemData | CompanyCollectionItemData;
export type GenericCollectionType = PropertyCollectionData | CompanyCollectionData;

export class CollectionItemDataHelper extends DataObjectHelper<GenericCollectionItemType> {
  id = () => "collectionItems-" + this.getCollection()?.id;

  displayName = () => {
    return this.getCollection()?.name.translate() ?? t("main.collection");
  };

  private collection: GenericCollectionType | undefined;

  constructor(collection: GenericCollectionType | undefined) {
    super();
    this.collection = collection;
  }

  propertyField = new FirebaseDataPropertyField<GenericCollectionItemType>(
    "property",
    t("main.property"),
    (item) => (item.parent?.parent instanceof PropertyData ? item.parent.parent : undefined),
    "business"
  );

  nameField = new FirebaseDataStringField<GenericCollectionItemType>("name", t("main.name"), (item) => item.name, "title");

  archived = new FirebaseDataBooleanField<GenericCollectionItemType>(
    "archived",
    t("main.isArchivedText"),
    (item) => item.archived || (item.parent?.parent instanceof PropertyData && item.parent.parent.archived),
    "archive",
    false
  );

  getCollection = () => this.collection;

  protected getFieldsForCollection(): DataObjectField<any, any>[] {
    const collection = this.getCollection();

    const fields: DataObjectField<any, any>[] = [];
    fields.push(this.propertyField);
    fields.push(this.nameField);
    fields.push(this.archived);

    if (collection?.connections.propertyFiles) {
      fields.push(
        new FirebaseDataFileDocumentField<GenericCollectionItemType>(
          "propertyFiles",
          t("main.propertyFiles"),
          (item) => {
            const files: PropertyFileData[] = [];
            if (item instanceof PropertyCollectionItemData && item.parent?.parent instanceof PropertyData) {
              for (const fileID of item.propertyFileIDs) {
                const file = item.parent.parent.files.addData(fileID);
                files.push(file);
              }
            }
            return files;
          },
          "folder"
        )
      );
    }
    fields.push(...(collection?.customFields.documents.map((field) => this.getDataObjectField(field, collection)) ?? []));
    return fields;
  }

  allFields() {
    return this.getFieldsForCollection();
  }

  getEditFields() {
    const collection = this.getCollection();
    return [this.nameField, ...(collection?.customFields.documents.map((field) => this.getDataObjectField(field, collection)) ?? [])];
  }

  getDataObjectField(field: CompanyCustomFieldData | PropertyCustomFieldData, collection: GenericCollectionType): DataObjectField<any, any> {
    switch (field.dataType) {
      case CustomFieldDataType.TEXT: {
        return new FirebaseDataStringField<GenericCollectionItemType>(field.id, field.name.translate(), (item) => this.getData(item, field), "edit");
      }
      case CustomFieldDataType.NUMBER: {
        return new FirebaseDataNumberField<GenericCollectionItemType>(field.id, field.name.translate(), (item) => this.getData(item, field), "pin");
      }
      case CustomFieldDataType.DATE: {
        return new FirebaseDataDateField<GenericCollectionItemType>(
          field.id,
          field.name.translate(),
          (item) => {
            const value = this.getData(item, field);
            return CustomFieldDateDataType.getDateFieldAsDate(value, field.dataOptions);
          },
          "event"
        );
      }
      case CustomFieldDataType.TAGS: {
        return new DataObjectTagField<GenericCollectionItemType>(
          field.id,
          field.name.translate(),
          (item) => {
            const value = this.getData(item, field);
            if (typeof value === "string") {
              return LocalData.getTagWithId(value);
            } else if (Array.isArray(value)) {
              const tags: Tag[] = [];
              for (const tagID of value) {
                const tag = LocalData.getTagWithId(tagID);
                if (tag) tags.push(tag);
              }
              return tags;
            }
            return undefined;
          },
          "label",
          true
        );
      }
      case CustomFieldDataType.CONTRACTORS: {
        return new FirebaseDataContractorField<GenericCollectionItemType>(
          field.id,
          field.name.translate(),
          (item) => this.getData(item, field),
          "construction",
          true
        );
      }
      case CustomFieldDataType.NORWEGIAN_STANDARDS: {
        return new FirebaseDataNorwegianStandardField<GenericCollectionItemType>(
          field.id,
          field.name.translate(),
          (data) => {
            const value = this.getData(data, field);
            if (typeof value === "string") {
              const option = SystemsAndComponentsUtilities.getOptionWithKey(undefined, value);
              return option ? { id: option.id, name: option.title } : undefined;
            }
            return undefined;
          },
          "flag"
        );
      }
      case CustomFieldDataType.COLLECTION_ITEM: {
        return new FirebaseDataCollectionItemFields<GenericCollectionItemType>(
          collection instanceof CompanyCollectionData ? collection.parent?.id : undefined,
          collection instanceof PropertyCollectionData ? collection.parent?.id : undefined,
          field.dataOptions.collectionID,
          field.id,
          field.name.translate(),
          (data) => this.getData(data, field),
          "flag"
        );
      }
      case CustomFieldDataType.CUSTOM_DATA_TYPE: {
        const collectionLoader = this.getDataTypeCollection();
        if (field.dataOptions.customDataTypeID && collectionLoader) {
          return new FirebaseDataCustomDataTypeFields<GenericCollectionItemType>(
            collectionLoader.addData(field.dataOptions.customDataTypeID),
            field.id,
            field.name.translate(),
            (data) => this.getData(data, field),
            "flag"
          );
        }
        return new FirebaseDataStringField<GenericCollectionItemType>(field.id, field.name.translate(), (item) => this.getData(item, field), "edit");
      }
      case CustomFieldDataType.TENANTS: {
        return new FirebaseDataUnitField<GenericCollectionItemType>(
          field.id,
          field.name.translate(),
          (item) => this.getData(item, field),
          "business"
        );
      }
      default: {
        return new FirebaseDataStringField<GenericCollectionItemType>(field.id, field.name.translate(), (item) => this.getData(item, field), "edit");
      }
    }
  }

  getData(data: GenericCollectionItemType, field: CompanyCustomFieldData | PropertyCustomFieldData): any {
    return data.fields[field.id] instanceof ValueDataSource ? (data.fields[field.id] as ValueDataSource).data : null;
  }

  getFileData(data: GenericCollectionItemType, field: CompanyCustomFieldData | PropertyCustomFieldData): any {
    const fieldVal = data.fields[field.id];
    if (fieldVal instanceof ValueDataSource) {
      const files: FirebaseFile[] = [];
      if (typeof fieldVal.data === "object") {
        Object.keys(fieldVal.data).forEach((key) => {
          const file = FirebaseFile.initialize(key, fieldVal.data[key]);
          files.push(file);
        });
      }
      return files;
    } else {
      return null;
    }
    //return data.fields[field.id] instanceof ValueDataSource ? (data.fields[field.id] as ValueDataSource).data : null;
  }

  override getAllDataTableDatas(documents: GenericCollectionItemType[]): DataTableData[] {
    const collection = this.getCollection();
    return documents.map((item) => {
      const property = item.parent?.parent instanceof PropertyData ? item.parent.parent : undefined;
      const propertyFiles = property ? item.propertyFileIDs.map((fileID) => property.files.addData(fileID)) : [];
      const globalFiles = item.fileIDs.map((fileID) => LocalData.files.addData(fileID));
      const files = [...propertyFiles, ...globalFiles];
      const data: DataTableData = {
        key: `${item.parent?.parent?.id}-${item.parent?.id}-${item.id}`,
        cells: {
          name: item.name,
          property: property?.name ?? "",
          visibleFor: Utilities.getReadAccessString(item.readAccess),
          propertyFiles: {
            rawData: files,
            element: <DataTableFileDocumentsCell files={files} />,
            rawString: files.map((file) => file.name).join(", "),
          },
        },
      };

      if (collection) {
        for (const fieldID of this.getAllDataTableHeaders()
          .map((header) => header.key)
          .filter((key) => !Object.keys(data.cells).includes(key))) {
          data.cells[fieldID] = CollectionUtilities.getTableData(item.fields[fieldID], collection.customFields.getData(fieldID), item);
        }
      }

      return data;
    });
  }

  override getAllDataTableHeaders(): DataTableHeader[] {
    const collection = this.getCollection();
    const headers: DataTableHeader[] = [
      {
        key: "name",
        title: t("main.name"),
      },
      {
        key: "visibleFor",
        title: t("main.visibleFor"),
      },
    ];
    if (collection?.parent instanceof PropertyData) {
      headers.push({
        key: "property",
        title: t("main.property"),
      });
      headers.push({
        key: "propertyFiles",
        title: t("main.files"),
      });
    }
    if (collection) {
      for (const field of collection.customFields.documents) {
        headers.push({
          key: field.id,
          title: field.name.translate(),
        });
      }
    }
    return headers;
  }

  getStandardHeaderElements(): string[] {
    return this.getCollection()?.previewFields ?? [];
  }

  override areAllLoaded(): boolean {
    return this.getCollection()?.customFields.loaded ?? true;
  }

  override loadAll(): Promise<any> {
    const collection = this.getCollection();
    if (!collection) return Promise.resolve();
    const promises: Promise<any>[] = [];
    if (collection instanceof PropertyCollectionData) {
      const property = collection?.parent instanceof PropertyData ? collection?.parent : undefined;
      const member = property?.members.getData(LocalData.currentUser.id);
      const memberRoles = member?.roles.map((role) => role.id);

      if (memberRoles?.length) {
        promises.push(
          new Promise((resolve) => {
            collection?.customFields.loadWithFilter(
              (ref) => query(ref, where("readAccess.roles", "array-contains-any", memberRoles)),
              () => {
                resolve(undefined);
              }
            );
          })
        );
      }
    } else if (collection instanceof CompanyCollectionData) {
      promises.push(collection.items.loadWithPromise());
    }

    if (!collection?.loaded) {
      promises.push(collection?.load());
    }
    return Promise.all(promises);
  }

  override getLinkTo(key: string): string | undefined {
    if (this.getCollection() instanceof PropertyCollectionData) {
      const keyElements = key.split("-");
      if (keyElements.length === 3) {
        return `/properties/${keyElements[0]}/collections/${keyElements[1]}/items/${keyElements[2]}`;
      }
    }
    return undefined;
  }

  getDataTypeCollection(): CollectionLoader<DataTypeData> | undefined {
    const collection = this.getCollection();
    if (collection?.parent instanceof PropertyData) {
      return collection.parent.dataTypes;
    } else if (collection?.parent instanceof CompanyData) {
      return collection.parent.dataTypes;
    }
    return undefined;
  }

  override getActionsForData = (data: GenericCollectionItemType): DataObjectAction<GenericCollectionItemType>[] => {
    const actions: DataObjectAction<GenericCollectionItemType>[] = [];
    const collection = this.getCollection();

    if (data.hasWriteAccess()) {
      actions.push({
        id: "changeAccess",
        iconName: "visibility",
        title: t("main.changeVisibility"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <ReadAccessPopup
                id={id}
                readAccess={dataObjects[0].readAccess}
                requiredAccess={collection?.requiredWriteAccess}
                onSelect={(access) => {
                  resolve({ dataObjects, data: access });
                }}
                context={{ propertyID: collection?.parent instanceof PropertyData ? collection.parent.id : undefined }}
                accessGroups={["roles", "contractorGroups", "propertyUnits"]}
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentPromise(t("main.savingData"), data.saveUpdate({ readAccess: Utilities.convertReadAccess(updateData) }));
        },
      });
      actions.push({
        id: "settings",
        iconName: "settings",
        title: t("main.settings"),
        performAction: () => {
          Popup.present((id) => <CollectionItemSettings item={data} id={id} />);
          return Promise.resolve();
        },
        maxSelected: 1,
      });

      if (!data.archived) {
        actions.push({
          id: "archive",
          iconName: "archive",
          title: t("main.archiveCollectionItem"),
          requiredConsent: (dataObjects) => {
            return new Promise((resolve, reject) => {
              if (dataObjects.length > 1) {
                PopupTemplates.presentVerifyAction(`${t("main.archiveCollectionItems")}?`, t("main.collectionItemsArchivationCaution"), () => {
                  resolve({ dataObjects });
                });
              } else {
                PopupTemplates.presentVerifyAction(`${t("main.archiveCollectionItem")}?`, t("main.collectionItemArchivationCaution"), () => {
                  resolve({ dataObjects });
                });
              }
            });
          },
          performAction: () => {
            return ToastUtilities.presentSavingToast(data.archive());
          },
        });
      } else {
        actions.push({
          id: "unarchive",
          iconName: "archive",
          title: t("main.unarchiveCollectionItem"),
          requiredConsent: (dataObjects) => {
            return new Promise((resolve, reject) => {
              if (dataObjects.length > 1) {
                PopupTemplates.presentVerifyAction(`${t("main.unarchiveCollectionItems")}?`, t("main.collectionItemsDearchivationCaution"), () => {
                  resolve({ dataObjects });
                });
              } else {
                PopupTemplates.presentVerifyAction(`${t("main.unarchiveCollectionItem")}?`, t("main.collectionItemDearchivationCaution"), () => {
                  resolve({ dataObjects });
                });
              }
            });
          },
          performAction: () => {
            return ToastUtilities.presentSavingToast(data.archive());
          },
        });
      }

      actions.push({
        id: "delete",
        iconName: "delete",
        title: t("main.delete") + " " + (collection?.name.translate() ?? ""),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyDeleteDatas(
              this,
              dataObjects.map((data) => data.name),
              () => {
                resolve({ dataObjects });
              },
              t("main.itemDeletionCaution")
            );
          });
        },
        performAction: () => {
          const promise = data
            .saveUpdate({
              deletedBy: { userID: LocalData.currentUser.id, date: new Date() },
            })
            .then(() => {
              data.deleted = true;
              this.collection?.items.removeData(data.id);
            });
          ToastUtilities.presentPromise(t("main.deleting"), promise, t("main.deleted"));
          return promise;
        },
      });
    }

    return actions;
  };

  getDefaultTableSortKey(): string | undefined {
    return "name";
  }

  getAllDatas(): GenericCollectionItemType[] {
    return this.getCollection()?.items.documents ?? [];
  }

  override saveUpdate(data: GenericCollectionItemType, updatedRawData: { [key: string]: any }): Promise<any> {
    const updateData: { [key: string]: any } = {};
    if (updatedRawData.name) {
      updateData.name = updatedRawData.name;
    }
    for (const fieldID in updatedRawData) {
      if (this.getCollection()?.customFields.hasData(fieldID)) {
        updateData[`fields.${fieldID}`] = {
          value: {
            data: updatedRawData[fieldID],
          },
        };
      }
    }

    return data.saveUpdate(updateData);
  }

  getUniqueIDForData(data: GenericCollectionItemType): string {
    return data.getCompletePath();
  }
}
